import React, { useState, useEffect, useCallback } from 'react';
import { Table, Space, Button, Tag, Empty, Checkbox, Pagination } from 'antd';
import { DeleteOutlined, ReadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import { setUserDetail } from '../../../redux/auth.reducer';
import './Notifications.css';

const MOBILE_BREAKPOINT = 768;
const PAGE_SIZE = 10;

/** Under 1 hour: "Just now" / "X minutes ago"; 1h to 24h: time only; over 24h: date and time */
function formatReceivedAt(value) {
  if (!value) return '—';
  const d = dayjs(value);
  if (!d.isValid()) return '—';
  const now = dayjs();
  const diffMs = now.diff(d);
  const oneHour = 60 * 60 * 1000;
  const oneDay = 24 * oneHour;
  if (diffMs < 60000) return 'Just now';
  if (diffMs < oneHour) {
    const mins = Math.floor(diffMs / 60000);
    return mins === 1 ? '1 minute ago' : `${mins} minutes ago`;
  }
  if (diffMs < oneDay) return d.format('h:mm A');
  return d.format('MMM D, YYYY h:mm A');
}

const refetchUser = async (dispatch) => {
  try {
    const res = await axios.get('/users/me');
    if (res.data?.user) {
      dispatch(setUserDetail({
        email: res.data.user.email,
        name: res.data.user.name,
        roles: res.data.user.roles || [],
        notificationsList: res.data.user.notificationsList || [],
        unreadNotifications: res.data.user.unreadNotifications ?? 0,
      }));
    }
  } catch (_) {}
};

export default function Notifications() {
  const dispatch = useDispatch();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
  const [mobilePage, setMobilePage] = useState(1);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setMobilePage(1);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/notifications');
      setList(res.data?.data ?? []);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load notifications';
      dispatch(setMessage({ message: msg, type: 'error' }));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async () => {
    if (selectedRowKeys.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axios.patch('/notifications/read', { ids: selectedRowKeys });
      dispatch(setMessage({ message: `${res.data?.updated ?? 0} marked as read`, type: 'success' }));
      setSelectedRowKeys([]);
      await fetchNotifications();
      await refetchUser(dispatch);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to mark as read';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axios.delete('/notifications', { data: { ids: selectedRowKeys } });
      dispatch(setMessage({ message: `${res.data?.deleted ?? 0} deleted`, type: 'success' }));
      setSelectedRowKeys([]);
      await fetchNotifications();
      await refetchUser(dispatch);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setActionLoading(false);
    }
  };

  const unreadIds = list.filter((n) => !n.readAt).map((n) => n._id);
  const hasUnreadSelected = selectedRowKeys.some((id) => unreadIds.includes(id));

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <span className={record.readAt ? 'notifications-cell-read' : 'notifications-cell-unread'}>
          {text}
        </span>
      ),
    },
    {
      title: 'Received at',
      dataIndex: 'receiveAt',
      key: 'receiveAt',
      width: 180,
      render: (v) => formatReceivedAt(v),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) =>
        record.readAt ? (
          <Tag color="default">Read</Tag>
        ) : (
          <Tag color="blue">Unread</Tag>
        ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const toggleCardSelection = (id) => {
    setSelectedRowKeys((prev) =>
      prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]
    );
  };

  const paginatedList = isMobile
    ? list.slice((mobilePage - 1) * PAGE_SIZE, mobilePage * PAGE_SIZE)
    : list;
  const totalPages = Math.ceil(list.length / PAGE_SIZE) || 1;

  return (
    <div className="notifications-content">
      <div className="notifications-header">
        <Space wrap size="small">
          <Button
            type="primary"
            icon={<ReadOutlined />}
            onClick={handleMarkAsRead}
            disabled={selectedRowKeys.length === 0 || !hasUnreadSelected}
            loading={actionLoading}
            className="app-btn-primary"
          >
            Mark selected as read
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            disabled={selectedRowKeys.length === 0}
            loading={actionLoading}
            className="app-btn-delete"
          >
            Delete selected
          </Button>
        </Space>
      </div>

      {isMobile ? (
        <div className="notifications-cards">
          {loading ? (
            <div className="notifications-cards-loading">Loading…</div>
          ) : list.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No notifications"
              style={{ margin: '24px 0' }}
            />
          ) : (
            <>
              <div className="notifications-card-list">
                {paginatedList.map((item) => (
                  <div
                    key={item._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleCardSelection(item._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleCardSelection(item._id);
                      }
                    }}
                    className={`notifications-card ${selectedRowKeys.includes(item._id) ? 'notifications-card-selected' : ''}`}
                    aria-pressed={selectedRowKeys.includes(item._id)}
                  >
                    <Checkbox
                      checked={selectedRowKeys.includes(item._id)}
                      onChange={() => toggleCardSelection(item._id)}
                      onClick={(e) => e.stopPropagation()}
                      className="notifications-card-checkbox"
                    />
                    <div className="notifications-card-body">
                      <div className={`notifications-card-title ${item.readAt ? 'notifications-cell-read' : 'notifications-cell-unread'}`}>
                        {item.title}
                      </div>
                      <div className="notifications-card-meta">
                        <span className="notifications-card-time">{formatReceivedAt(item.receiveAt)}</span>
                        {item.readAt ? (
                          <Tag color="default">Read</Tag>
                        ) : (
                          <Tag color="blue">Unread</Tag>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="notifications-cards-pagination">
                  <Pagination
                    current={mobilePage}
                    total={list.length}
                    pageSize={PAGE_SIZE}
                    onChange={setMobilePage}
                    showSizeChanger={false}
                    size="small"
                    showTotal={(total) => `Total ${total} notifications`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="notifications-table-wrapper">
          <Table
            rowKey="_id"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={list}
            loading={loading}
            scroll={{ x: 400 }}
            pagination={{
              pageSize: PAGE_SIZE,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} notifications`,
              size: 'small',
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No notifications"
                  style={{ margin: '24px 0' }}
                />
              ),
            }}
          />
        </div>
      )}
    </div>
  );
}
